import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Engine HP from 1/4-mile ET / Trap Speed
export default function EngineHorsepowerCalc() {
  const [mode, setMode] = useState('et'); // et | trap
  const [weight, setWeight] = useState(3200);
  const [et, setEt] = useState(13.5);
  const [trap, setTrap] = useState(100);
  const [weightUnit, setWeightUnit] = useState('lb');

  const result = useMemo(() => {
    const factors = { lb: 1, kg: 2.20462, t: 2204.62, ton: 2000, stone: 14 };
    const w = Number(weight) * (factors[weightUnit] || 1);
    if (mode === 'et') {
      // Fox / Hale: HP = weight / (ET/5.825)^3
      const hp = w / Math.pow(Number(et) / 5.825, 3);
      return { hp: hp.toFixed(1), formula: `HP = Weight / (ET / 5.825)^3` };
    }
    // Trap-Speed (Huntington): HP = weight * (mph/234)^3
    const hp = w * Math.pow(Number(trap) / 234, 3);
    return { hp: hp.toFixed(1), formula: `HP = Weight × (MPH / 234)^3` };
  }, [mode, weight, et, trap, weightUnit]);

  return (
    <CalcShell look="cnet" title="Engine Horsepower Calculator" subtitle="Estimate engine horsepower using 1/4-mile Elapsed-Time (ET) or Trap-Speed formulas.">
      <div className="flex gap-2 border-b mb-4">
        {[['et','ET Method'],['trap','Trap-Speed Method']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Vehicle Weight (with driver)</label>
            <div className="flex gap-2">
              <input type="number" value={weight} onChange={e => setWeight(e.target.value)} className="flex-1" data-testid="ehp-weight"/>
              <select value={weightUnit} onChange={e => setWeightUnit(e.target.value)} className="w-52">
                <option value="lb">lb — pound</option>
                <option value="kg">kg — kilogram</option>
                <option value="t">t — metric ton</option>
                <option value="ton">ton — short ton</option>
                <option value="stone">stone (14 lb)</option>
              </select>
            </div>
          </div>
          {mode === 'et' ? (
            <div className="field"><label>1/4-Mile Elapsed Time (sec)</label>
              <input type="number" step="0.01" value={et} onChange={e => setEt(e.target.value)} data-testid="ehp-et"/>
            </div>
          ) : (
            <div className="field"><label>1/4-Mile Trap Speed (mph)</label>
              <input type="number" step="0.1" value={trap} onChange={e => setTrap(e.target.value)} data-testid="ehp-trap"/>
            </div>
          )}
        </div>
        <div>
          <div className="p-5 rounded-xl bg-orange-50 border border-orange-200 text-center">
            <div className="text-sm text-slate-600">Estimated Horsepower</div>
            <div className="text-4xl font-bold text-orange-700" data-testid="ehp-result">{result.hp} HP</div>
            <div className="text-sm text-slate-600 mt-2">
              ≈ <b>{(parseFloat(result.hp) * 745.7).toLocaleString(undefined, { maximumFractionDigits: 1 })}</b> watts
              &nbsp;·&nbsp; <b>{(parseFloat(result.hp) * 0.7457).toFixed(3)}</b> kW
              &nbsp;·&nbsp; <b>{(parseFloat(result.hp) * 1.01387).toFixed(2)}</b> PS (metric)
              &nbsp;·&nbsp; <b>{(parseFloat(result.hp) * 550).toFixed(1)}</b> ft·lb/s
            </div>
            <div className="text-xs text-slate-500 mt-2 font-mono">{result.formula}</div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
